import type { ITenantsResponseV2 } from '@frontegg/rest-api';

const NO_TENANTS: ITenantsResponseV2[] = [];

/** Frontegg's tenant list, or an empty one while it is missing. Frontegg types the list as always
 *  present, but loadTenants stores the response's `tenants` field as is, and its client reads a 2xx
 *  body it cannot parse as `{}`, so the list can be undefined (Sentry UI-33Y). One shared empty list
 *  keeps memos and effects keyed on it from rerunning. */
export function tenantListOrEmpty(tenants: ITenantsResponseV2[] | null | undefined): ITenantsResponseV2[] {
  return Array.isArray(tenants) ? tenants : NO_TENANTS;
}

/** Parses Frontegg tenant metadata, a JSON string or an object, to reach the subdomain. */
export function parseMetadata(metadata: unknown): { subdomain?: string } | null {
  try {
    return typeof metadata === 'string' ? JSON.parse(metadata) : (metadata as { subdomain?: string } | null);
  } catch {
    return null;
  }
}

// "boomtown" from "boomtown.foresightdata.com"; localhost maps to the dev tenant
export function getCurrentSubdomain(): string {
  const hostname = window.location.hostname;
  return hostname === 'localhost' ? 'dev' : hostname.split('.')[0];
}

/** The customer this host belongs to: the membership whose subdomain matches,
 *  or the only membership a single-tenant user has. Undefined until known. */
export function resolveCustomerTenantId(
  tenants: ITenantsResponseV2[],
  hasMultipleMemberships: boolean,
  ownTenantId: string | undefined
): string | undefined {
  const currentSubdomain = getCurrentSubdomain();
  const matching = tenants.find((tenant) => parseMetadata(tenant.metadata)?.subdomain === currentSubdomain);
  if (matching) {
    return matching.tenantId;
  }
  return hasMultipleMemberships ? undefined : ownTenantId;
}
